import { ACTION_FREQUENCY_TYPE, CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, FREQUENCY_TYPE, getDurationHours, getInsightsForNode, LinqNode, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, OUTPUT_VALUE_SOURCE, WORK_TYPE } from "./linqset-model"
import { ACTION_TYPE, Organisation } from "./organisation"
import { informationTypeOf } from "./information-type"

// defines action nodes in the process. These are collapsed nodes that include the role and system information, so they can be used to model the processing of items through a process.
export interface AnalysisNode extends LinqNode {
    durationSeconds:number
    processType:ACTION_FREQUENCY_TYPE
    roles:AnalysisRole[]
    systems:string[]
    heldBy?:string[]           // outputs: the systems attached to the information, supplying or holding it
    heldAs?:NODE_TYPE          // outputs: how it is held, as its type says or, typed as plain information, as LINQ reads it
    heldAsRead?:boolean        // that is LINQ's read (see information-type.ts)

    isStarting:boolean
    setsFrequency?:boolean      // actions only: how often it happens is set on it, rather than worked out from its inputs
    actionType?:ACTION_TYPE     // actions only: manual, automated, both or external, as worked out by LINQ
    tagKeys?:{[groupKey:string]:string}   // actions only: the tag the action carries in each tag group
    numPeople:number
    value:number
    items:number
    capacity: number
    hours: number
    revenue: number
    contributedValue?:number   // the share of the value it helps create, so steps add up (value is what it supports)
    contributedRevenue?:number
    atRiskValue?:number        // the value lost if it stopped, as its items have no other route
    atRiskRevenue?:number
    cost: number
    carbon: number
    totalHours:number
    totalCost:number
    totalCarbon:number
    outputsSupported:number
    friction:number             // friction from alternate paths through a model. calculated as a proportion of most efficient path

    // outputs: the value set on them and the value derived from how they are used (see information-value.ts)
    userValue:number            // value per item set by the user, 0 when none is set
    userRevenue:number          // revenue per item set by the user, 0 when none is set
    joinedOut?:boolean          // joined into another model, so its value comes from there and none is set on it
    valueSource?:OUTPUT_VALUE_SOURCE|null   // where a set value came from, when it wasn't typed by the user
    derivedValue:number         // value per item derived from use, 0 to 100 across the organisation
    consumers:number            // actions and outcomes that use the output directly
    consumerModels:number       // other models the output is used in
    outcomesReached:number      // outcomes downstream of the output
    externalConsumers:number    // external organisations performing the actions that use it
    assetKey?:string            // the information asset it is a state of, if any

    // actions: exception work, as the user said or as LINQ read it from a detour (see exception-work.ts)
    exceptionWork:boolean
    workType?:WORK_TYPE|null
    workKind?:WORK_TYPE         // the kind of work, as the user said or as LINQ read it (see WORK_TYPE)
    waitHours?:number           // where items wait: the hours they spend waiting there a year, in place of hours of work
    onDetour:boolean
    detourSplitKey?:string      // where items leave the main flow (an action or information node)
    detourJoinKey?:string       // where they return to it
    detourShare:number          // of the items leaving the split

    centrality:number   // generated using yfiles graph analysis
}

export interface AnalysisRole {
    _key:string
    systemKeys:string[]
}

export interface AnalysisEdge {
    _key: string
    modelKey:string
    from:string
    to:string
    type:EDGE_TYPE
    informationKey:string
    informationName:string
    informationType?:NODE_TYPE  // how the information is held as it passes: a file, paper, word of mouth, or in a system
    informationTypeRead?:boolean    // the type is LINQ's read, the information being typed as plain information (see information-type.ts)
    informationInSystem?:boolean    // a system is attached to the information, supplying or holding it
    informationSystems?:string[]    // the systems attached to it
    informationAssetKey?:string     // the information asset the information is a state of, if any
    percentage:number       // percentage of items sent down this edge
    proportion:number       // proportion of all items sent from preceding node
    items:number

    centrality:number   // generated using yfiles graph analysis

}

// an output realising an outcome is only use of it once a value or revenue is set on it (see information-value.ts)
export const isUseOf = (e:AnalysisEdge, output:AnalysisNode) =>
    e.type !== EDGE_TYPE.REALISE || (output.userValue||0) > 0 || (output.userRevenue||0) > 0

export const createAnalysisNode = (node:LinqsetNode, workspaceKey:string):AnalysisNode => {
    const i = getInsightsForNode(node, node.modelKey, workspaceKey)
    return {
        _key: node._key,
        modelKey: node.modelKey,
        name: node.name,
        superType:node.superType,
        type: node.type,
        durationSeconds: getDurationHours(node.properties?.duration)*3600,
        processType: node.properties?.actionProcessType,
        roles: [],
        systems: [],
        isStarting: i?.isInput,
        setsFrequency: node.superType === NODE_SUPERTYPE.ACTION && !!node.properties?.frequency && node.properties.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED && (node.properties.frequency.ocurrences ?? -1) >= 0,
        actionType: i?.actionType,
        tagKeys: node.properties?.tagKeys,
        numPeople: i?.numPeople||0,
        value:i?.value||0,
        items:i?.numberProcessed||0,
        capacity: i?.modelledCapacity||0,
        hours: i?.hours||0,
        revenue: i?.revenue||0,
        contributedValue: i?.contributedValue||0,
        contributedRevenue: i?.contributedRevenue||0,
        atRiskValue: i?.atRiskValue||0,
        atRiskRevenue: i?.atRiskRevenue||0,
        outputsSupported: i?.outputsSupported||0,
        friction: 0,
        // one joined into another model has no value of its own, whatever is set on it
        userValue: i?.joinedOut ? 0 : node.properties?.outputValue||0,
        userRevenue: i?.joinedOut ? 0 : node.properties?.outputRevenue||0,
        joinedOut: !!i?.joinedOut,
        valueSource: node.properties?.outputValueSource||null,
        derivedValue: i?.derivedValue||0,
        consumers: i?.consumers||0,
        consumerModels: i?.consumerModels||0,
        outcomesReached: i?.outcomesReached||0,
        externalConsumers: i?.externalConsumers||0,
        assetKey: node.informationAssetKey,
        exceptionWork: !!i?.exceptionWork,
        workType: node.properties?.workType||null,
        workKind: i?.workKind,
        waitHours: i?.waitHours||0,
        onDetour: !!i?.onDetour,
        detourSplitKey: i?.detourSplitKey,
        detourJoinKey: i?.detourJoinKey,
        detourShare: i?.detourShare||0,
        cost: i?.allocatedCostsByType.total||0,
        carbon: i?.allocatedCarbonByScope.total||0,
        totalHours: i?.totalHours||0,
        totalCost: i?.totalAllocatedCostsByType.total||0,
        totalCarbon: i?.totalCarbon||0,
        centrality: 0
    } as AnalysisNode
}

const createActionEdge = (fromAction:AnalysisNode, edge1:LinqsetEdge, infoNode:LinqsetNode, edge2:LinqsetEdge, systems:string[], held:{type:NODE_TYPE, read:boolean}):AnalysisEdge => {
    return {
        _key: edge1._key,
        modelKey: fromAction.modelKey,
        from: fromAction._key,
        to: edge2.targetKey,
        type: edge2.type === EDGE_TYPE.USED_IN?EDGE_TYPE.USED_IN:EDGE_TYPE.ACTION_SUMMARY,
        informationKey: infoNode._key,
        informationName: infoNode.name,
        informationType: held.type,
        informationTypeRead: held.read,
        informationInSystem: systems.length > 0,
        informationSystems: systems,
        informationAssetKey: infoNode.informationAssetKey,
        proportion: 1,
        percentage: edge2.type === EDGE_TYPE.USED_IN?0:edge1.outputPercentage*edge2.outputPercentage/100,
        items: edge2.type === EDGE_TYPE.USED_IN?0:fromAction.items*edge1.outputPercentage*edge2.outputPercentage/10000,
        centrality: 0
    } as AnalysisEdge
}

export const createAnalysisEdge = (edge:LinqsetEdge, node:AnalysisNode):AnalysisEdge => {
    return {
        _key: edge._key,
        modelKey: edge.modelKey,
        from: edge.sourceKey,
        to: edge.targetKey,
        type: edge.type,
        informationKey: node._key,
        informationName: node.name,
        informationType: node.type,
        informationAssetKey: node.assetKey,
        proportion: 1,
        percentage: edge.outputPercentage,
        items: node.items,
        centrality: 0
    } as AnalysisEdge
}

// groups items by a key, keeping their order
const groupBy = <T>(items:T[], key:(item:T) => string) => items.reduce((m, item) => {
    const k = key(item)
    const list = m.get(k)
    if (list) list.push(item)
    else m.set(k, [item])
    return m
}, new Map<string, T[]>())

// the process graph of a workspace's models: each action, with who performs it, linked to the actions its information
// supports, and to the outputs it creates and the outcomes they realise. Edges are looked up by the node they leave or
// reach, as organisations can have thousands of nodes
export const createAnalysisGraph = (nodes:LinqsetNode[], edges:LinqsetEdge[], workspaceKey:string) => {
    const actions = nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION)
    const informations = new Map(nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION).map(n => [n._key, n]))
    const nodesByKey = new Map(nodes.map(n => [n._key, n]))
    const creates = groupBy(edges.filter(e => e.type === EDGE_TYPE.CREATE), e => e.sourceKey)
    const supports = groupBy(edges.filter(e => e.type === EDGE_TYPE.SUPPORT || e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN), e => e.sourceKey)
    const performs = groupBy(edges.filter(e => e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE), e => e.targetKey)
    // a role's systems are those used by the role where it performs the action: each place a role is drawn has its own
    const uses = groupBy(edges.filter(e => e.type === EDGE_TYPE.USEBY), e => e.targetKey + '|' + e.targetVisualisationKey)
    const realises = groupBy(edges.filter(e => e.type === EDGE_TYPE.REALISE), e => e.sourceKey)
    // the systems attached to each piece of information, supplying or holding it
    const supplied = groupBy(edges.filter(e => e.type === EDGE_TYPE.SUPPLY), e => e.targetKey)
    const systemsHolding = (key:string) => [...new Set((supplied.get(key) || []).map(e => e.sourceKey))]
    // how it is held: its type, or LINQ's read from the systems attached and its name
    const heldAs = (info:LinqsetNode) => informationTypeOf(info, systemsHolding(info._key).map(k => nodesByKey.get(k)).filter((n):n is LinqsetNode => !!n))

    const nodeMap:{[key:string]:AnalysisNode} = {} // outputs and outcomes already added

    return actions.reduce((g:{nodes:AnalysisNode[], edges:AnalysisEdge[]}, n) => {
        const analysisAction = createAnalysisNode(n, workspaceKey)
        // process roles and systems
        ;(performs.get(n._key) || []).forEach(e => {
            if (e.type === EDGE_TYPE.AUTOMATE) analysisAction.systems.push(e.sourceKey)
            else {
                analysisAction.roles.push({
                    _key: e.sourceKey,
                    systemKeys: [...new Set((uses.get(e.sourceKey + '|' + e.sourceVisualisationKey) || []).map(u => u.sourceKey))]
                })
            }
        })

        g.nodes.push(analysisAction)
        let totalPercentage = 0
        const infoEdges:AnalysisEdge[] = []
        // process outgoing edges
        ;(creates.get(n._key) || []).forEach(e => {
            const info = informations.get(e.targetKey)
            if (info) {
                (supports.get(info._key) || []).forEach(e2 => {
                    const ie = createActionEdge(analysisAction, e, info, e2, systemsHolding(info._key), heldAs(info))
                    g.edges.push(ie)
                    if (e2.type !== EDGE_TYPE.USED_IN) {
                        infoEdges.push(ie)
                        totalPercentage += ie.percentage
                    }
                })

                const i = getInsightsForNode(info, info.modelKey, workspaceKey)
                if (i?.isOutput) {
                    // an output created by more than one action realises its outcomes once
                    const firstTime = !nodeMap[info._key]
                    if (firstTime) {
                        nodeMap[info._key] = createAnalysisNode(info, workspaceKey)
                        nodeMap[info._key].heldBy = systemsHolding(info._key)
                        const held = heldAs(info)
                        nodeMap[info._key].heldAs = held.type
                        nodeMap[info._key].heldAsRead = held.read
                        g.nodes.push(nodeMap[info._key])
                    }
                    const ae = createAnalysisEdge(e, nodeMap[info._key])
                    g.edges.push(ae)
                    infoEdges.push(ae)
                    totalPercentage += ae.percentage

                    if (firstTime) {
                        let realisePercentage = 0
                        const realiseEdges:AnalysisEdge[] = []

                        ;(realises.get(info._key) || []).forEach(r => {
                            if (!nodeMap[r.targetKey]) {
                                const outcome = nodesByKey.get(r.targetKey)
                                if (outcome) {
                                    nodeMap[outcome._key] = createAnalysisNode(outcome, workspaceKey)
                                    g.nodes.push(nodeMap[outcome._key])
                                }
                            }
                            if (nodeMap[r.targetKey]) {
                                const realise = createAnalysisEdge(r, nodeMap[info._key])
                                g.edges.push(realise)
                                realisePercentage += realise.percentage
                                realiseEdges.push(realise)
                            }
                        })
                        realiseEdges.forEach(e => e.proportion = realisePercentage?e.percentage/realisePercentage:1/realiseEdges.length)
                    }
                }
            }
        })
        infoEdges.forEach(e => e.proportion = totalPercentage?e.percentage/totalPercentage:1/infoEdges.length)

        return g
    }, {nodes: [], edges: []})
}

// the edges a workspace has, from the current state's and its projects' together: for a model a project has changed,
// its own copies, and for the rest the current state's (as a Linqset reads them). A sandbox has only its own. Without
// this a changed model's edges would all appear twice
export const edgesForWorkspace = (edges:LinqsetEdge[], organisation:Pick<Organisation, 'models'>, project?:{_key:string, sandbox?:boolean}) => {
    if (!project || project._key === CURRENT_STATE_PROJECT_KEY) return edges.filter(e => e.projectKey === CURRENT_STATE_PROJECT_KEY)
    if (project.sandbox) return edges.filter(e => e.projectKey === project._key)
    const changed = changedModels(organisation, project)
    return edges.filter(e => e.projectKey === (changed.has(e.modelKey) ? project._key : CURRENT_STATE_PROJECT_KEY))
}

const changedModels = (organisation:Pick<Organisation, 'models'>, project:{_key:string}) =>
    new Set((organisation.models || []).filter(m => (m.projects || []).includes(project._key)).map(m => m._key))

// where an action's items go next: each step and the information they go to it as
export const nextSteps = (action:AnalysisNode, edges:AnalysisEdge[], actionNames:Map<string, string>):{ step:string, via:string }[] => {
    const next = edges.filter(e => e.from === action._key && e.to !== action._key && actionNames.has(e.to))
        .map(e => ({ step: actionNames.get(e.to)!, via: e.informationName || '' }))
    return next.filter((n, i) => next.findIndex(m => m.step === n.step && m.via === n.via) === i)
}

// the nodes a workspace has. Nodes are shared between the current state and its projects, and a project's edges say
// which of a changed model's nodes it keeps: a current state step the project's edges no longer reach was removed in
// it, so it is left out rather than appearing unconnected. The model's own nodes nothing is joined to yet, made in the
// workspace, stay, as a Linqset keeps them. Models the project hasn't changed keep all their nodes
export const nodesForWorkspace = (nodes:LinqsetNode[], edges:LinqsetEdge[], organisation:Pick<Organisation, 'models'>, project?:{_key:string, sandbox?:boolean}) => {
    if (!project || project._key === CURRENT_STATE_PROJECT_KEY) return nodes.filter(n => n.projectKey === CURRENT_STATE_PROJECT_KEY)
    const own = edgesForWorkspace(edges, organisation, project)
    const joined = new Set(own.flatMap(e => [e.sourceKey, e.targetKey]))
    const changed = project.sandbox ? undefined : changedModels(organisation, project)
    return nodes.filter(n => {
        if (changed && !changed.has(n.modelKey)) return n.projectKey === CURRENT_STATE_PROJECT_KEY
        return joined.has(n._key) || n.projectKey === project._key
    })
}
